import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { X, Check, Search } from "lucide-react";
import {
  fetchActiveLanguages,
  fetchTranslations,
  setSelectedLanguage,
} from "../store/language/action";
import { baseURL } from "../util/Config";
import useTranslation from "../hooks/useTranslation";
import translateIcon from "../assets/images/translate.png";
import "./LanguageSwitcher.css";

const buildIconSrc = (iconPath) => {
  if (!iconPath) return null;
  if (/^https?:\/\//i.test(iconPath)) return iconPath;
  return `${baseURL || ""}${iconPath}`;
};

/** embedded kept for Admin.js compatibility — same header chip + modal UI as famychat-bd */
const LanguageSwitcher = ({ embedded = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    activeLanguages,
    selectedLanguageCode,
    selectedLanguage,
    loadingTranslations,
  } = useSelector((state) => state.language);

  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    dispatch(fetchActiveLanguages());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedLanguageCode) return;
    dispatch(fetchTranslations(selectedLanguageCode));
  }, [selectedLanguageCode, dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim().toLowerCase());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearchInput("");
    setDebouncedSearch("");
  }, []);

  const handleSelect = useCallback(
    (code) => {
      dispatch(setSelectedLanguage(code));
      handleClose();
    },
    [dispatch, handleClose],
  );

  const filteredLanguages = useMemo(() => {
    const q = debouncedSearch;
    const list = !q
      ? [...(activeLanguages || [])]
      : (activeLanguages || []).filter((lang) => {
          const name = (
            lang.localLanguageTitle ||
            lang.languageTitle ||
            ""
          ).toLowerCase();
          const code = (lang.languageCode || "").toLowerCase();
          return name.includes(q) || code.includes(q);
        });

    list.sort((a, b) => {
      const aSel =
        a.languageCode?.toLowerCase() === selectedLanguageCode?.toLowerCase()
          ? 0
          : 1;
      const bSel =
        b.languageCode?.toLowerCase() === selectedLanguageCode?.toLowerCase()
          ? 0
          : 1;
      return aSel - bSel;
    });
    return list;
  }, [activeLanguages, debouncedSearch, selectedLanguageCode]);

  const currentLabel =
    selectedLanguage?.localLanguageTitle ||
    selectedLanguage?.languageTitle ||
    selectedLanguageCode ||
    "English";

  const modal =
    open &&
    createPortal(
      <div
        className="lang-modal-overlay"
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
          background: "rgba(0, 0, 0, 0.75)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          zIndex: 99999,
        }}
        onClick={handleClose}
      >
        <div
          className="lang-modal"
          style={{
            width: "100%",
            maxWidth: 384,
            display: "flex",
            flexDirection: "column",
            background: "#381559",
            borderRadius: 20,
            border: "none",
            boxShadow: "none",
            maxHeight: "78vh",
            overflow: "hidden",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              flexShrink: 0,
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "14px 20px 12px",
              borderBottom: "1px solid #FFFFFF1A",
            }}
          >
            <h2
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#FFFFFF",
                margin: 0,
              }}
            >
              {t("select_language", "Select Language")}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              aria-label={t("close", "Close")}
              style={{
                position: "absolute",
                right: 16,
                top: "50%",
                transform: "translateY(-50%)",
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.14)",
                color: "#FFFFFF",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}
            >
              <X size={16} />
            </button>
          </div>

          {/* Search + list */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              padding: "14px 16px 8px",
            }}
          >
            <div style={{ position: "relative", marginBottom: 14, flexShrink: 0 }}>
              <Search
                size={16}
                style={{
                  position: "absolute",
                  left: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#FFFFFF80",
                  pointerEvents: "none",
                }}
              />
              <input
                type="text"
                className="lang-modal-search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t(
                  "search_language_by_name_code",
                  "Search language by name & code",
                )}
                style={{
                  width: "100%",
                  height: 44,
                  padding: "0 16px 0 40px",
                  borderRadius: 10,
                  background: "#FFFFFF1A",
                  border: "0.5px solid #FFFFFF26",
                  color: "#FFFFFF",
                  fontSize: 13,
                  fontFamily: "inherit",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div
              className="lang-modal-list"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {!activeLanguages?.length ? (
                <p
                  style={{
                    color: "#FFFFFF80",
                    fontSize: 13,
                    textAlign: "center",
                    margin: "24px 0",
                  }}
                >
                  {t("loading_languages", "Loading languages...")}
                </p>
              ) : filteredLanguages.length === 0 ? (
                <p
                  style={{
                    color: "#FFFFFF80",
                    fontSize: 13,
                    textAlign: "center",
                    margin: "24px 0",
                  }}
                >
                  {t("no_languages_found", "No languages found")}
                </p>
              ) : (
                filteredLanguages.map((lang) => {
                  const isActive =
                    lang.languageCode?.toLowerCase() ===
                    selectedLanguageCode?.toLowerCase();
                  const code = (lang.languageCode || "").toUpperCase();
                  const iconSrc = buildIconSrc(lang.languageIcon);

                  return (
                    <button
                      key={lang._id || lang.languageCode}
                      type="button"
                      onClick={() => handleSelect(lang.languageCode)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        width: "100%",
                        padding: "12px 12px",
                        marginBottom: 4,
                        borderRadius: 14,
                        border: isActive ? "none" : "0.5px solid transparent",
                        background: isActive
                          ? "linear-gradient(90deg, #FD297E 0%, #6B01FF 100%)"
                          : "transparent",
                        cursor: "pointer",
                        textAlign: "left",
                        fontFamily: "inherit",
                      }}
                    >
                      {iconSrc ? (
                        <img
                          src={iconSrc}
                          alt=""
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            objectFit: "cover",
                            flexShrink: 0,
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <span
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            background: isActive
                              ? "rgba(255,255,255,0.2)"
                              : "linear-gradient(135deg, #FD297E, #6B01FF)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#FFFFFF",
                            flexShrink: 0,
                          }}
                        >
                          {code.slice(0, 2)}
                        </span>
                      )}
                      <span
                        style={{
                          flex: 1,
                          fontSize: 14,
                          fontWeight: isActive ? 600 : 500,
                          color: "#FFFFFF",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {lang.localLanguageTitle || lang.languageTitle}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: isActive ? "#FFFFFF" : "#FFFFFF80",
                          letterSpacing: "0.04em",
                          flexShrink: 0,
                        }}
                      >
                        {code}
                      </span>
                      {isActive && (
                        <span
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            background: "#FFFFFF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Check
                            size={13}
                            style={{ color: "#6B01FF" }}
                            strokeWidth={3}
                          />
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <div
      className={`lang-switcher-wrapper${embedded ? " embedded" : ""}`}
      style={embedded ? undefined : { position: "fixed", top: 14, right: 14, zIndex: 20 }}
    >
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={t("switch_language", "Switch language")}
        aria-expanded={open}
        aria-haspopup="dialog"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 14px",
          background: "#FFFFFF1A",
          border: "1px solid #FFFFFF26",
          borderRadius: 12,
          cursor: "pointer",
          color: "#FFFFFF",
          fontSize: 14,
          fontWeight: 500,
          whiteSpace: "nowrap",
          flexShrink: 0,
          fontFamily: "inherit",
        }}
      >
        <img
          src={translateIcon}
          alt=""
          style={{
            width: 18,
            height: 18,
            objectFit: "contain",
            display: "block",
            flexShrink: 0,
          }}
        />
        <span style={{ color: "#FFFFFF" }}>{currentLabel}</span>
        {loadingTranslations ? (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#FD297E",
              display: "inline-block",
            }}
          />
        ) : (
          <svg
            width={14}
            height={14}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="2.5"
            style={{ flexShrink: 0 }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
      </button>
      {modal}
    </div>
  );
};

export default LanguageSwitcher;
